/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router-dom';
import {
  fidpAnonymous,
  fidpEmail,
  fidpGitHub,
  fidpGoogle,
  fidpEnterprise,
  fidpMicrosoft,
  googleCaptchaSiteKey,
  disableGoogleCaptcha,
} from 'config';
import { getVSCodeAuthRequestURL } from 'hooks/auth';
import { ChoreoSignInConfig, VSCodeChoreoSignInConfig } from 'utils/auth';
import { getGrowthHackingParams } from 'utils/growthHacking/utm';
import {
  getVSCodePluginParams,
  hasVSCodePluginParams,
} from 'utils/vsCodePlugin/utm';
import logger from 'utils/logger';
import generateUUID from 'utils/uuid';
import { useAuthContext } from '@asgardeo/auth-react';

interface UseSignInFlowArgs {
  /** Where to land after signing in; goes into the OAuth `state`. */
  returnToUrl: string;
  returnToOrg: string;
  isInvitation: boolean;
  confirmationOrg: string;
  confirmationKey: string;
  queryParams: URLSearchParams;
}

/**
 * The sign in handlers shared by the login page and the auth dialog: one per
 * identity provider, each encoding its own `state` parameter for the redirect.
 *
 * Extracted from the login page so the dialog can drive the same flows without
 * duplicating the `state` encoding, the VS Code plugin branch or the invisible
 * reCAPTCHA wiring.
 */
export default function useSignInFlow(args: UseSignInFlowArgs) {
  const {
    returnToUrl,
    returnToOrg,
    isInvitation,
    confirmationOrg,
    confirmationKey,
    queryParams,
  } = args;

  const history = useHistory();
  const { signIn } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaRef = createRef<ReCAPTCHA>();

  const handleSignIn = async (
    fidp: string,
    stateParam: { [key: string]: string },
    captchaInfo?: string,
    username?: string
  ) => {
    let encodedStateParam = '';
    let modifiedStateParams = { ...stateParam };
    // For more info https://github.com/wso2-enterprise/choreo/issues/12584
    // Looking for utm_* & visitor_id
    const queryFromReturnURL = new URL(window.location.origin + returnToUrl);
    const growthHackingParams = getGrowthHackingParams([
      ...queryParams,
      ...queryFromReturnURL.searchParams,
    ]);

    const vsCodePluginParams = getVSCodePluginParams([...queryParams]);

    const isVSCodePluginSignIn = hasVSCodePluginParams(vsCodePluginParams);

    const hasGrowthHackingParams = growthHackingParams.length > 0;
    if (hasGrowthHackingParams) {
      growthHackingParams.forEach(([key, value]) => {
        modifiedStateParams[key] = value;
      });
    }
    if (Object.keys(modifiedStateParams).length > 0) {
      if (isInvitation) {
        modifiedStateParams = {
          ...stateParam,
          confirmationOrg,
          confirmationKey,
        };
      }
      encodedStateParam = btoa(JSON.stringify(modifiedStateParams));
    }
    try {
      setIsLoading(true);
      const config: ChoreoSignInConfig = {
        fidp,
        state: encodedStateParam,
      };
      if (username) {
        config.username = username;
      }
      if (captchaInfo) {
        config.captchaInfo = captchaInfo;
      }

      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };

      if (isVSCodePluginSignIn) {
        const vsCodeConfig: VSCodeChoreoSignInConfig = {
          ...config,
          clientID: vsCodePluginParams.client_id,
          state: vsCodePluginParams.state,
          codeChallengeVsCode: vsCodePluginParams.code_challenge,
        };
        const authRequestURL = getVSCodeAuthRequestURL(vsCodeConfig);
        window.location.href = authRequestURL;
      } else {
        signIn(
          config,
          undefined,
          undefined,
          undefined,
          undefined,
          tokenRequestConfig
        );
      }
    } catch (signInError) {
      logger.error(signInError);
      history.push('/internal-error');
      setIsLoading(false);
    }
  };

  const handleEmailLogin = () => {
    const stateParamObj = {
      fidpId: fidpEmail,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpEmail, stateParamObj);
  };

  const handleGithubLogin = () => {
    const stateParamObj = {
      fidpId: fidpGitHub,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpGitHub, stateParamObj);
  };

  const handleGoogleLogin = () => {
    const stateParamObj = {
      fidpId: fidpGoogle,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpGoogle, stateParamObj);
  };

  const handleMicrosoftLogin = () => {
    const stateParamObj = {
      fidpId: fidpMicrosoft,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpMicrosoft, stateParamObj);
  };

  const handleEnterpriseLogin = (userEmail: string) => {
    const fidpParam = fidpEnterprise || 'EnterpriseIDP';
    const stateParamObj = {
      fidpId: fidpParam,
      returnToUrl,
      returnToOrg,
    };
    handleSignIn(fidpParam, stateParamObj, undefined, userEmail);
  };

  const handleCaptchaChange = (token: string | null) => {
    const stateParamObj = {
      fidpId: fidpAnonymous,
      returnToUrl,
      returnToOrg,
    };
    const captchaInfoParamObj: { [key: string]: string } = {};
    if (googleCaptchaSiteKey && token) {
      captchaInfoParamObj.siteKey = googleCaptchaSiteKey;
      captchaInfoParamObj.captchaToken = token;
    }
    const encodedCaptchaInfoParam = btoa(JSON.stringify(captchaInfoParamObj));
    handleSignIn(fidpAnonymous, stateParamObj, encodedCaptchaInfoParam);
  };

  const handleAnonymousLogin = () => {
    if (disableGoogleCaptcha === 'true') {
      handleCaptchaChange(null);
    } else {
      recaptchaRef.current?.execute();
    }
  };

  return {
    isLoading,
    recaptchaRef,
    handleSignIn,
    handleEmailLogin,
    handleGithubLogin,
    handleGoogleLogin,
    handleMicrosoftLogin,
    handleEnterpriseLogin,
    handleAnonymousLogin,
    handleCaptchaChange,
  };
}
